import React, { useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  EVENT_TERMS_TITLE,
  EVENT_TERMS_VERSION,
  EVENT_TERMS_CLAUSES,
  TERMS_SUMMARY_BULLETS,
  TERMS_SECTIONS,
} from "../content/eventsTermsContent";

/* ─── Summary bullet icons for event detail section ─── */
var BULLET_ICONS = ["⚠️", "🔒", "💼", "📋", "💳", "🚗", "📄", "✅"];

/** Shared: renders all 52 clauses grouped by section (used by modal and standalone). */
export function EventTermsFullContent({ className }) {
  return (
    <div className={className || "ev-terms-full"}>
      <p className="ev-terms-full__version">Version {EVENT_TERMS_VERSION}</p>
      <h2 className="ev-terms-full__main-title">{EVENT_TERMS_TITLE}</h2>
      {TERMS_SECTIONS.map(function (section) {
        var clauses = EVENT_TERMS_CLAUSES.filter(function (c) {
          return c.n >= section.from && c.n <= section.to;
        });
        return (
          <div key={section.id} className="ev-terms-full__group">
            <h3 className="ev-terms-full__group-title">
              <span className="ev-terms-full__group-num">{section.from}–{section.to}</span>
              {section.label}
            </h3>
            <ol className="ev-terms-full__list">
              {clauses.map(function (c) {
                return (
                  <li key={c.n} className="ev-terms-full__clause">
                    <h4 className="ev-terms-full__clause-title">
                      {c.n}. {c.title}
                    </h4>
                    <p className="ev-terms-full__clause-body">{c.body}</p>
                  </li>
                );
              })}
            </ol>
          </div>
        );
      })}
    </div>
  );
}

/** Modal with scrollable full T&C, grouped sections, ESC to close. */
export function EventTermsModal({ open, onClose }) {
  useEffect(
    function () {
      if (!open) return;
      var prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return function () {
        document.body.style.overflow = prev;
      };
    },
    [open]
  );

  var onKey = useCallback(
    function (e) {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(
    function () {
      if (!open) return;
      document.addEventListener("keydown", onKey);
      return function () { document.removeEventListener("keydown", onKey); };
    },
    [open, onKey]
  );

  if (!open) return null;

  return (
    <div
      className="ev-terms-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ev-terms-modal-title"
    >
      <div className="ev-terms-modal__backdrop" onClick={onClose} />
      <div className="ev-terms-modal__panel">
        <header className="ev-terms-modal__header">
          <div className="ev-terms-modal__header-left">
            <span className="ev-terms-modal__shield">🛡</span>
            <div>
              <h2 id="ev-terms-modal-title" className="ev-terms-modal__title">
                Terms &amp; Conditions
              </h2>
              <p className="ev-terms-modal__subtitle">
                {EVENT_TERMS_CLAUSES.length} clauses · v{EVENT_TERMS_VERSION}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="ev-terms-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {/* Section quick-nav inside modal */}
        <div className="ev-terms-modal__section-nav">
          {TERMS_SECTIONS.map(function (s) {
            return (
              <a
                key={s.id}
                href={"#modal-" + s.id}
                className="ev-terms-modal__section-pill"
              >
                {s.label}
              </a>
            );
          })}
        </div>

        <div className="ev-terms-modal__scroll">
          <div className="ev-terms-full">
            {TERMS_SECTIONS.map(function (section) {
              var clauses = EVENT_TERMS_CLAUSES.filter(function (c) {
                return c.n >= section.from && c.n <= section.to;
              });
              return (
                <div
                  key={section.id}
                  id={"modal-" + section.id}
                  className="ev-terms-full__group"
                >
                  <h3 className="ev-terms-full__group-title">
                    <span className="ev-terms-full__group-num">
                      {section.from}–{section.to}
                    </span>
                    {section.label}
                  </h3>
                  <ol className="ev-terms-full__list">
                    {clauses.map(function (c) {
                      return (
                        <li key={c.n} className="ev-terms-full__clause">
                          <h4 className="ev-terms-full__clause-title">
                            {c.n}. {c.title}
                          </h4>
                          <p className="ev-terms-full__clause-body">{c.body}</p>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              );
            })}
          </div>
        </div>

        <footer className="ev-terms-modal__footer">
          <Link
            to="/events/terms-and-conditions"
            className="ev-terms-modal__open-page"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open full page ↗
          </Link>
          <button
            type="button"
            className="bk-btn bk-btn--primary"
            onClick={onClose}
          >
            I Understand &amp; Close
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * Layer 1 — Event detail page: summary section with icon bullets + open modal.
 * @param {{ eventName?: string, eventType?: string, organizerName?: string, insuranceEnabled?: boolean, anchorId?: string }} props
 */
export default function EventTermsAndConditions(props) {
  var eventName = props.eventName;
  var insuranceEnabled = props.insuranceEnabled;
  var anchorId = props.anchorId || "event-terms";

  var _s = React.useState(false);
  var modalOpen = _s[0];
  var setModalOpen = _s[1];

  return (
    <section className="ev-terms-section" id={anchorId}>
      {/* Header row */}
      <div className="ev-terms-section__head">
        <span className="ev-terms-section__shield">🛡</span>
        <div>
          <h2 className="ev-terms-section__title">Terms &amp; Conditions</h2>
          {eventName && (
            <p className="ev-terms-section__context">Applies to: {eventName}</p>
          )}
        </div>
      </div>

      {insuranceEnabled && (
        <div className="ev-terms-section__note">
          <span>📄</span>
          Optional insurance may be available for this event; if you purchase it, coverage is subject to the insurer&apos;s policy wording and not guaranteed by ParkAdda.
        </div>
      )}

      {/* Key points */}
      <ul className="ev-terms-section__bullets">
        {TERMS_SUMMARY_BULLETS.map(function (line, i) {
          return (
            <li key={i} className="ev-terms-section__bullet">
              <span className="ev-terms-section__bullet-icon" aria-hidden="true">
                {BULLET_ICONS[i] || "•"}
              </span>
              <span>{line}</span>
            </li>
          );
        })}
      </ul>

      {/* Actions */}
      <div className="ev-terms-section__actions">
        <button
          type="button"
          className="ev-terms-section__primary-btn"
          onClick={() => setModalOpen(true)}
        >
          Read Full Terms &amp; Conditions
        </button>
        <Link to="/events/terms-and-conditions" className="ev-terms-section__page-link">
          Open in separate page ↗
        </Link>
      </div>

      <EventTermsModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
}

/** Layer 2 — Checkout step 4: compact T&C acceptance strip before Pay. */
export function EventTermsBookingBlock(props) {
  var termsAccepted = props.termsAccepted;
  var onTermsChange = props.onTermsChange;
  var onOpenFull = props.onOpenFull;

  return (
    <div
      className={"ev-terms-booking" + (termsAccepted ? " ev-terms-booking--accepted" : "")}
      id="booking-terms-anchor"
    >
      <label
        className={"ev-terms-booking__check" + (termsAccepted ? " ev-terms-booking__check--done" : "")}
      >
        <input
          type="checkbox"
          checked={!!termsAccepted}
          onChange={function (e) { onTermsChange(e.target.checked); }}
        />
        <span className="ev-terms-booking__check-label">
          I have read and agree to the ParkAdda&nbsp;
          {onOpenFull ? (
            <button
              type="button"
              className="ev-terms-booking__text-btn ev-terms-booking__text-btn--inline"
              onClick={onOpenFull}
            >
              Terms &amp; Conditions (Events)
            </button>
          ) : (
            <Link
              to="/events/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="ev-terms-booking__text-btn ev-terms-booking__text-btn--inline"
            >
              Terms &amp; Conditions (Events)
            </Link>
          )}
          .
        </span>
      </label>

      {!termsAccepted && (
        <p className="ev-terms-booking__nudge">
          ☝ Please accept the terms above to enable payment.
        </p>
      )}
    </div>
  );
}
